import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  User, 
  Settings, 
  Bell, 
  Shield,
  CreditCard,
  Smartphone,
  LogOut,
  ArrowRight,
  HardDrive
} from 'lucide-react'
import CacheManager from '../../components/PWA/CacheManager'
import OfflineDataManager from '../../components/PWA/OfflineDataManager'

const UserCenter: React.FC = () => {
  const userInfo = {
    name: '张三',
    email: 'zhangsan@example.com',
    avatar: '👨‍💼',
    level: 'VIP用户',
    joinDate: '2024-01-01'
  }

  const menuItems = [
    {
      icon: User,
      title: '个人信息',
      description: '管理您的个人资料',
      path: '/user/profile'
    },
    {
      icon: Smartphone,
      title: '我的设备',
      description: '查看和管理您的设备',
      path: '/user/devices'
    },
    {
      icon: CreditCard,
      title: '订单管理',
      description: '查看订单和支付记录',
      path: '/user/orders'
    },
    {
      icon: Shield,
      title: '安全设置',
      description: '管理账户安全设置',
      path: '/user/security'
    },
    {
      icon: Bell,
      title: '消息通知',
      description: '管理通知偏好设置',
      path: '/user/notifications'
    },
    {
      icon: Settings,
      title: '账户设置',
      description: '管理账户相关设置',
      path: '/user/settings'
    },
    {
      icon: HardDrive,
      title: '缓存管理',
      description: '管理应用缓存和存储',
      path: '/user/cache'
    }
  ]

  const recentActivities = [
    {
      action: '登录账户',
      time: '2024-01-15 10:30',
      type: 'login'
    },
    {
      action: '更新个人信息',
      time: '2024-01-14 15:20',
      type: 'update'
    },
    {
      action: '购买CoreTrust手机',
      time: '2024-01-13 09:15',
      type: 'purchase'
    },
    {
      action: '修改密码',
      time: '2024-01-12 14:45',
      type: 'security'
    }
  ]

  const quickActions = [
    {
      title: '查看订单',
      description: '查看最近的订单状态',
      icon: CreditCard,
      action: 'view-orders'
    },
    {
      title: '设备管理',
      description: '管理您的CoreTrust设备',
      icon: Smartphone,
      action: 'manage-devices'
    },
    {
      title: '安全中心',
      description: '检查账户安全状态',
      icon: Shield,
      action: 'security-check'
    },
    {
      title: '联系客服',
      description: '获取技术支持',
      icon: User,
      action: 'contact-support'
    }
  ]

  return (
    <>
      <Helmet>
        <title>用户中心 - DeepLink Limited</title>
        <meta name="description" content="DeepLink用户中心，管理您的账户、设备、订单等信息。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex items-center space-x-6"
            >
              <div className="text-6xl">{userInfo.avatar}</div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                  欢迎回来，{userInfo.name}
                </h1>
                <p className="text-gray-300 mb-2">{userInfo.email}</p>
                <div className="flex items-center space-x-4">
                  <span className="px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-full text-sm font-semibold">
                    {userInfo.level}
                  </span>
                  <span className="text-gray-400 text-sm">
                    加入时间：{userInfo.joinDate}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="py-12 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                快速操作
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickActions.map((action, index) => {
                const Icon = action.icon
                return (
                  <motion.button
                    key={action.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-6 text-left hover:shadow-lg transition-shadow duration-300 group"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      {action.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      {action.description}
                    </p>
                  </motion.button>
                )
              })}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Menu Items */}
              <div className="lg:col-span-2">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className="mb-8"
                >
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    账户管理
                  </h2>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {menuItems.map((item, index) => {
                    const Icon = item.icon
                    return (
                      <motion.div
                        key={item.title}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                              {item.title}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                              {item.description}
                            </p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* Recent Activities */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                    最近活动
                  </h3>
                  <div className="space-y-4">
                    {recentActivities.map((activity, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${
                          activity.type === 'login' ? 'bg-green-500' :
                          activity.type === 'update' ? 'bg-blue-500' :
                          activity.type === 'purchase' ? 'bg-purple-500' :
                          'bg-orange-500'
                        }`}></div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {activity.action}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            {activity.time}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Account Stats */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                    账户统计
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">设备数量</span>
                      <span className="font-bold text-gray-900 dark:text-white">2</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">订单总数</span>
                      <span className="font-bold text-gray-900 dark:text-white">5</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">使用天数</span>
                      <span className="font-bold text-gray-900 dark:text-white">15</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">安全等级</span>
                      <span className="font-bold text-green-600 dark:text-green-400">高</span>
                    </div>
                  </div>
                </motion.div>

                {/* Logout */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                  className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-6"
                >
                  <h3 className="text-lg font-bold text-red-900 dark:text-red-400 mb-4">
                    安全退出
                  </h3>
                  <p className="text-red-700 dark:text-red-300 text-sm mb-4">
                    退出登录以保护您的账户安全
                  </p>
                  <button className="w-full bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors duration-300 flex items-center justify-center space-x-2">
                    <LogOut className="w-4 h-4" />
                    <span>退出登录</span>
                  </button>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* 缓存管理 */}
        <section className="py-20 bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                缓存管理
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                管理应用缓存，优化存储空间和性能
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto">
              <CacheManager />
            </div>
          </div>
        </section>

        {/* 离线数据管理 */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                离线数据管理
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                管理离线数据，确保在网络不稳定时也能正常使用应用
              </p>
            </motion.div>

            <div className="max-w-6xl mx-auto">
              <OfflineDataManager />
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default UserCenter